import React, { useEffect } from 'react';
import {
  Box, Button, Flex, IconButton, Menu, MenuButton, MenuDivider, MenuItem, MenuList,
  useBreakpointValue, useColorModeValue,
} from '@chakra-ui/react';
import { motion, useDragControls, useReducedMotion } from 'framer-motion';
import {
  FiX, FiUser, FiSliders, FiSettings, FiUsers, FiClock, FiMoreHorizontal, FiChevronDown,
} from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import { useAppearance } from '../theme/AppearanceProvider';
import useCurrentUser from '../hooks/useCurrentUser';
import { visibleSecondaryTools, visibleTools } from './toolsNav';
import { PayType } from '../models/user';

const MotionBox = motion(Box);

// item 455 — LAYER 2: tools/settings/dashboards render as a full-screen panel that
// slides UP over the still-mounted layer-0 canvas (projects + the Basix Six). The
// canvas never unmounts, so closing the panel returns you to exactly where you
// were — scroll, expanded sections, half-typed forms and all. Sits at z 1250:
// above the shell, BELOW wizard modals (Chakra modal 1400 — a wizard opened from
// Settings floats over the panel) and far below the PIN lock (2000).
//
// item 1160 (Chad: "I hate the way the UX is on these top modals, and the grab
// does not work") — the ONE-BAR SHEET redesign:
//   · the old pill+X row and the centered chip row merged into a single compact
//     header: chips left, More bucket + X right. Half the chrome.
//   · the ENTIRE header bar is the drag-to-dismiss surface (the 44px pill was
//     both invisible and, worse, only armed when Aurora+motion+no-reduced-motion
//     all held — that gate is gone; drag is direct manipulation, not an
//     animation, so it's ALWAYS armed. Only the entrance/exit slide respects
//     the motion settings).
//   · in Aurora the sheet is INSET from the top with rounded shoulders so a
//     sliver of the live canvas peeks through — it finally *reads* as a layer
//     over your work, and clicking that exposed rim closes it. The Accountant
//     skin stays flat, square, full-bleed (zero-decoration rule).
interface LayerPanelProps {
  onClose: () => void;
  // the panel route's pathname — drives the switcher chips (item 498/500). Breadcrumbs removed (item 692).
  headerPath: string;
}

const LayerPanel: React.FC<LayerPanelProps> = ({ onClose, headerPath, children }) => {
  const { aurora, motion: effectsMotion } = useAppearance();
  const reducedMotion = useReducedMotion();
  const dragControls = useDragControls();
  const history = useHistory();
  const {
    isTeam, isAdmin, isOutsideSales, isExecutive, user,
  } = useCurrentUser();
  const barBorder = useColorModeValue('blackAlpha.100', 'whiteAlpha.200');
  const switcherBar = useColorModeValue('blackAlpha.50', 'whiteAlpha.50');
  // item 1160b — the segmented-nav TRACK backing + the below-lg collapse into one menu
  const trackBg = useColorModeValue('blackAlpha.100', 'whiteAlpha.100');
  const isCompact = useBreakpointValue({ base: true, lg: false }) ?? false;

  // item 475 — the tools switcher: every tool as a chip in the modal header so you can hop
  // straight to another tool without exiting. Clicking a layer-2 tool swaps the panel in place;
  // a canvas tool navigates there (closing the panel) — either way, no manual exit needed.
  const tools = visibleTools({
    isTeam,
    // hourly AND a rate actually plugged in — that's who gets a timesheet
    isHourly: user?.payType === PayType.Hourly && (user?.hourlyRate ?? 0) > 0,
    isExecutive, // item 594 — the top switcher must ALSO hide the financial tools (reports, accountant)
    isIndependentContractor: user?.isIndependentContractor === true
      || user?.payType === PayType.IndependentContractor, // item 1079/1083
    isAdmin, // item 1080 — admins review ALL timesheets
  });

  // item 500 (Chad) — the panel is shared by TOOLS and by USER-SETTINGS routes; the top strip
  // should reflect which. On a personal route (profile / appearance / settings / users) show a
  // USER-OPTIONS switcher instead of the tool chips — same modal, right context.
  // item 500 REVERTED (Chad, 2026-07-14) — the Item catalog is a TOOL again, so it rides the
  // TOOLS switcher (top-nav quick-switch), NOT the user-settings strip. /items is off this list.
  // item 502 — key this + the active-chip state off headerPath (the panel's OWN route, frozen by
  // AnimatePresence during the close animation), NOT the live pathname — otherwise closing a
  // settings route flashes the TOOLS chips as it animates out (the router already switched back).
  const isUserSettings = ['/settings', '/users', '/user', '/appearance']
    .some((p) => headerPath === p || headerPath.startsWith(`${p}/`));
  const userChips = [
    ...(user?.id ? [{
      key: 'profile', name: 'My Profile', icon: <FiUser />, link: `/user/${user.id}`,
    }] : []),
    {
      key: 'appearance', name: 'Appearance', icon: <FiSliders />, link: '/appearance',
    },
    ...(!isOutsideSales ? [{
      key: 'settings', name: 'Settings', icon: <FiSettings />, link: '/settings',
    }] : []),
    ...(isAdmin && !isTeam ? [{
      // item 1020 — another user's form (/user/:id) belongs to Users; My Profile still wins
      // its own exact link via the exact-match-first rule below.
      key: 'users', name: 'Users', icon: <FiUsers />, link: '/users', match: ['/user'],
    }] : []),
  ];
  let chips: { key: string; name: string; icon: React.ReactElement; link: string; match?: string[]; group?: string }[] = isUserSettings ? userChips : tools;

  // item 1020/1022 (Chad) — the strip must ALWAYS answer "where am I?". A chip's active test is
  // exact-link first, then its match prefixes. Secondary surfaces (CRM / Chat / Live Dashboard /
  // Mailbox) live in the BUCKET (a More menu at the strip's end); when you're ON one, the bucket
  // button itself becomes the lit chip carrying its name. Anything matched by NEITHER (e.g.
  // Timesheet for a non-hourly seat) still injects a one-off self-highlighted chip.
  const hit = (p: string) => headerPath === p || headerPath.startsWith(`${p}/`);
  const chipMatches = (c: { link: string; match?: string[] }) => hit(c.link) || (c.match ?? []).some(hit);
  const secondary = isUserSettings ? [] : visibleSecondaryTools({ isTeam, isExecutive });
  const activeSecondary = secondary.find(chipMatches);
  let currentKey = (chips.find((c) => headerPath === c.link) ?? chips.find(chipMatches))?.key;
  if (!currentKey && !activeSecondary) {
    const orphan = [
      {
        key: 'timesheet', name: 'Timesheet', icon: <FiClock />, link: '/timesheet',
      },
    ].find(chipMatches);
    if (orphan) {
      chips = [orphan, ...chips];
      currentKey = orphan.key;
    }
  }
  // item 1160b — what the collapsed mobile menu button wears: the current tool's identity
  const activeChip = chips.find((c) => c.key === currentKey) ?? activeSecondary;
  // Accountant skin + reduced-motion/effects get an instant, undecorated swap
  const animate = aurora && effectsMotion && !reducedMotion;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <MotionBox
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      zIndex={1250}
      display="flex"
      flexDirection="column"
      bg="transparent"
      initial={animate ? { y: '100%' } : false}
      animate={{ y: 0 }}
      exit={animate ? { y: '100%' } : undefined}
      transition={{ duration: animate ? 0.32 : 0, ease: [0.22, 1, 0.36, 1] }}
      // drag starts ONLY from the header bar (dragListener off) — otherwise every
      // mouse-drag on grids/text inside the panel would move the whole surface
      drag="y"
      dragListener={false}
      dragControls={dragControls}
      dragConstraints={{ top: 0, bottom: 0 }}
      dragElastic={{ top: 0, bottom: 0.5 }}
      onDragEnd={(_e: any, info: any) => {
        if (info.offset.y > 140 || info.velocity.y > 900) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Tool panel"
    >
      {/* item 1160 — the exposed rim: the live canvas peeks through above the sheet's
          rounded shoulders (Aurora only); clicking it closes, same as Esc / X / drag. */}
      {aurora && (
        <Box
          h={{ base: '10px', md: '14px' }}
          flexShrink={0}
          cursor="pointer"
          onClick={onClose}
          aria-hidden
        />
      )}
      <Flex
        direction="column"
        flex="1"
        minH={0}
        bg="var(--bx-app-canvas)"
        borderTopRadius={aurora ? '16px' : 0}
        boxShadow={aurora ? '0 -18px 60px rgba(0,0,0,0.45)' : undefined}
        borderTopWidth={aurora ? '1px' : 0}
        borderColor={barBorder}
        overflow="hidden"
      >
        {/* item 1160 — ONE header bar, BALANCED like a real top nav (Chad: "disproportionate…
            you just left justified it"): identity anchor left, chips CENTERED, X right — the
            three zones flex equally so the chips sit dead-center regardless of edge widths.
            The whole bar is the drag-to-dismiss handle (grab anywhere that isn't a button). */}
        <Flex
          align="center"
          px={3}
          py={2}
          borderBottomWidth="1px"
          borderBottomColor={barBorder}
          bg={switcherBar}
          cursor="grab"
          sx={{ touchAction: 'none', '&:active': { cursor: 'grabbing' } }}
          onPointerDown={(e) => {
            // a press that lands on a chip/menu/X is a click, never a drag
            if ((e.target as HTMLElement).closest('button')) return;
            dragControls.start(e as unknown as PointerEvent);
          }}
        >
          {/* left zone — quiet context label so the bar has an identity, not dead space */}
          <Flex flex={1} minW={0} align="center" pointerEvents="none" display={{ base: 'none', md: 'flex' }}>
            <Box
              fontSize="10px"
              fontWeight="bold"
              letterSpacing="0.14em"
              textTransform="uppercase"
              color="gray.500"
              whiteSpace="nowrap"
            >
              {isUserSettings ? 'Your settings' : 'Tools'}
            </Box>
          </Flex>
          {/* item 1160b (Chad: "larger, and bucket when in mobile") — the chips live inside ONE
              rounded TRACK (Linear/Vercel segmented-nav style) so the strip reads as a single
              instrument, not a button forest. Below lg the track collapses into ONE menu button
              carrying the current tool's name — no wrapped chip rows on small screens. */}
          {isCompact ? (
            <Menu placement="bottom">
              <MenuButton
                as={Button}
                size="md"
                fontSize="sm"
                borderRadius="full"
                flexShrink={0}
                colorScheme="brandGreen"
                leftIcon={activeChip?.icon ?? <FiMoreHorizontal />}
                rightIcon={<FiChevronDown />}
                textTransform="capitalize"
              >
                {activeChip?.name ?? 'Menu'}
              </MenuButton>
              <MenuList zIndex={1300} minW="220px">
                {chips.map((t) => (
                  <MenuItem
                    key={t.key}
                    icon={t.icon}
                    fontSize="sm"
                    fontWeight={t.key === currentKey ? 'bold' : undefined}
                    onClick={() => { if (t.key !== currentKey) history.push(t.link); }}
                  >
                    {t.name}
                  </MenuItem>
                ))}
                {secondary.length > 0 && <MenuDivider />}
                {secondary.map((t) => (
                  <MenuItem
                    key={t.key}
                    icon={t.icon}
                    fontSize="sm"
                    fontWeight={t.key === activeSecondary?.key ? 'bold' : undefined}
                    onClick={() => { if (t.key !== activeSecondary?.key) history.push(t.link); }}
                  >
                    {t.name}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          ) : (
            <Flex
              flexShrink={1}
              minW={0}
              align="center"
              bg={trackBg}
              borderRadius="full"
              p="3px"
              overflowX="auto"
              sx={{ gap: '2px', '::-webkit-scrollbar': { height: '4px' } }}
            >
              {(() => {
                // item 1022 (Chad, "option 1") — chunk the chips into their groups with a thin
                // separator between chunks so the strip reads as a few families, not a button forest.
                // Group order is fixed (sales → ops → money); ungrouped chips (the user strip, the
                // orphan fallback) render first, unseparated.
                const order = ['sales', 'ops', 'money'];
                const sorted = [...chips].sort((a, b) => order.indexOf(a.group ?? '') - order.indexOf(b.group ?? ''));
                const out: React.ReactNode[] = [];
                sorted.forEach((t, i) => {
                  const prev = sorted[i - 1];
                  if (i > 0 && t.group !== prev?.group) {
                    out.push(<Box key={`sep-${t.key}`} w="1px" alignSelf="stretch" my="8px" bg={barBorder} flexShrink={0} />);
                  }
                  const active = t.key === currentKey;
                  out.push(
                    // item 1020 — the active chip carries the brand accent so "where am I?" reads at
                    // a glance instead of a barely-different gray.
                    <Button
                      key={t.key}
                      size="md"
                      fontSize="sm"
                      h="38px"
                      borderRadius="full"
                      flexShrink={0}
                      leftIcon={t.icon}
                      variant={active ? 'solid' : 'ghost'}
                      colorScheme={active ? 'brandGreen' : undefined}
                      textTransform="capitalize"
                      onClick={() => { if (!active) history.push(t.link); }}
                    >
                      {t.name}
                    </Button>,
                  );
                });
                return out;
              })()}
              {/* item 1022 (Chad) — the BUCKET: secondary surfaces (CRM / Chat / Live Dashboard /
                  Mailbox) behind one More menu; on one of them the bucket IS the lit chip. */}
              {secondary.length > 0 && (
                <>
                  <Box w="1px" alignSelf="stretch" my="8px" bg={barBorder} flexShrink={0} />
                  <Menu placement="bottom-end">
                    <MenuButton
                      as={Button}
                      size="md"
                      fontSize="sm"
                      h="38px"
                      borderRadius="full"
                      flexShrink={0}
                      leftIcon={activeSecondary ? activeSecondary.icon : <FiMoreHorizontal />}
                      rightIcon={<FiChevronDown />}
                      variant={activeSecondary ? 'solid' : 'ghost'}
                      colorScheme={activeSecondary ? 'brandGreen' : undefined}
                    >
                      {activeSecondary ? activeSecondary.name : 'More'}
                    </MenuButton>
                    <MenuList zIndex={1300} minW="200px">
                      {secondary.map((t) => (
                        <MenuItem
                          key={t.key}
                          icon={t.icon}
                          fontSize="sm"
                          onClick={() => { if (t.key !== activeSecondary?.key) history.push(t.link); }}
                        >
                          {t.name}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                </>
              )}
            </Flex>
          )}
          {/* right zone — mirrors the left so the chips stay centered; X anchors the corner */}
          {/* item 1183 dart#3 (Chad, mobile) — ml auto anchors the X to the corner even when
              the left label zone is hidden and this zone has no flex share */}
          <Flex flex={{ base: 'none', md: 1 }} minW={0} justify="flex-end" ml="auto">
            <IconButton
              aria-label="Close panel"
              icon={<FiX />}
              size="sm"
              variant="ghost"
              ml={1}
              flexShrink={0}
              onClick={onClose}
            />
          </Flex>
        </Flex>
        {/* item 470/474 — generous LEFT/RIGHT + BOTTOM breathing room (Chad: ~100px), but a
            TIGHT top so the page header sits right under the header bar (item 474 — the 100px top
            left too much dead space above the title). */}
        {/* item 1505 (Chad, iPhone screenshots of /accountant: cards clipped left, Approve button
            outside its card) — overflowY alone computes overflow-x:auto, so any row that ran wide
            stretched the PANEL's horizontal scroll range and let the whole page pan sideways.
            overflowX hidden pins the panel; wide ledgers keep their OWN overflowX="auto" scrollers.
            The phone gutter now matches the canvas column (px 4) so AuroraPageShell's -4 bleed
            lands flush instead of 4px short; the bottom pad clears the iOS home indicator
            (env() is 0 everywhere else). id'd so the mobile e2e gate can assert it never widens. */}
        <Box
          id="basix-layer-scroll"
          flex="1"
          minH={0}
          overflowY="auto"
          overflowX="hidden"
          px={{ base: 4, md: '100px' }}
          pt={{ base: 3, md: 5 }}
          pb={{ base: 'calc(1.5rem + env(safe-area-inset-bottom))', md: '100px' }}
        >
          {children}
        </Box>
      </Flex>
    </MotionBox>
  );
};

export default LayerPanel;
